import { ComponentModel } from '../view-model/component-model';
import { BaseModel, ModelWithChildren } from '../view-model/base-model';
import { FieldModel } from '../view-model/field-model';
import { SectionModel } from '../view-model/section-model';
import { GroupModel } from '../view-model/group-model';
import { CodelistModel } from '../view-model/type/codelist-model';
import { AmountModel } from '../view-model/type/amount-model';
import { DateModel } from '../view-model/type/date-model';
import { TimeModel } from '../view-model/type/time-model';
import { MetadataOffertypeCodelistModel } from '../view-model/type/metadata-offertype-codelist-model';
import { IdModel } from '../view-model/type/id-model';
import { IdRefModel } from '../view-model/type/id-ref.model';
import { MeasureModel } from '../view-model/type/measure-model';
import { IndicatorModel } from '../view-model/type/indicator-model';
import { RepeatableFieldModel } from '../view-model/repeatable-field-model';
import { RepeatableGroupModel } from '../view-model/repeatable-group-model';
import { MultilingualModel } from '../view-model/type/multilingual-model';
import { RepeatableSectionModel } from '../view-model/repeatable-section-model';
import { Bt300BrDeKmuModel } from '../view-model/type/bt300-br-de-kmu-model';
import { ConceptModel, ConceptNode } from '../types/concept-node';

interface SerializedModel {
  value: any;
  modelIndex: number;
  type: string;
  children: SerializedModel[];
  relatedTimeModelId?: number;
  relatedDateModelId?: number;
  parentId: number;
  noticeNode: any;
}

export class ModelSerializationUtils {
  constructor() {}

  public static serializeComponentModel(componentModel: ComponentModel): string {
    const rootCompModel = {} as any;
    rootCompModel.noticeId = componentModel.noticeId;
    rootCompModel.activeLanguages = componentModel.activeLanguages;
    rootCompModel.formType = componentModel.formType;
    rootCompModel.children = componentModel.children?.map(child => this.serializeModel(child));
    return JSON.stringify(rootCompModel);
  }

  public static deserializeComponentModel(serializedModel: string): ComponentModel {
    const parsedModel = JSON.parse(serializedModel);
    const indexToModelMap = new Map<number, BaseModel>();

    const rootCompModel = new ComponentModel();
    rootCompModel.noticeId = parsedModel.noticeId;
    rootCompModel.activeLanguages = parsedModel.activeLanguages;
    rootCompModel.formType = parsedModel.formType;
    rootCompModel.children = parsedModel.children?.map(child =>
      this.deserializeModel(child, indexToModelMap)
    );
    rootCompModel.children.forEach(child =>
      this.restoreComponentModelReference(child, indexToModelMap)
    );

    return rootCompModel;
  }

  private static serializeModel(baseModel: any): SerializedModel {
    return {
      value: baseModel.value,
      modelIndex: baseModel.modelIndex,
      type: baseModel.constructor.name,
      children: baseModel.children?.map(child => this.serializeModel(child)),
      parentId: baseModel.parent?.modelIndex,
      noticeNode: baseModel.noticeNode,
      relatedDateModelId: baseModel.relatedDateModel?.modelIndex,
      relatedTimeModelId: baseModel.relatedTimeModel?.modelIndex,
    };
  }

  private static deserializeModel(
    serializedModel: SerializedModel,
    indexToModelMap: Map<number, BaseModel>
  ): BaseModel {
    const model = this.instantiateModel(serializedModel);
    (model as any).serializedModel = serializedModel;
    if (serializedModel.value !== undefined) {
      (model as FieldModel<any>).value = serializedModel.value;
    }
    model.modelIndex = serializedModel.modelIndex;
    model.parentId = serializedModel.parentId;
    indexToModelMap.set(model.modelIndex, model);
    model.noticeNode = serializedModel.noticeNode;
    if (serializedModel.children) {
      (model as any)._children = serializedModel.children?.map(child =>
        this.deserializeModel(child, indexToModelMap)
      );
    }

    return model;
  }

  private static fillIndexToModelMap(
    componentModel: ComponentModel,
    indexToModelMap: Map<number, BaseModel>
  ) {
    componentModel.children?.forEach(child => this._fillIndexToModelMap(child, indexToModelMap));
  }

  private static _fillIndexToModelMap(
    baseModel: BaseModel,
    indexToModelMap: Map<number, BaseModel>
  ) {
    indexToModelMap.set(baseModel.modelIndex, baseModel);
    (baseModel as ModelWithChildren).children?.forEach(child =>
      this._fillIndexToModelMap(child, indexToModelMap)
    );
  }

  private static restoreComponentModelReference(
    model: BaseModel,
    indexToModelMap: Map<number, BaseModel>
  ): BaseModel {
    const serializedModel = (model as any).serializedModel as SerializedModel;
    if (serializedModel?.parentId !== undefined) {
      model.parent = indexToModelMap.get(serializedModel.parentId) as ModelWithChildren;
    }
    if (serializedModel?.relatedDateModelId !== undefined) {
      const restored = indexToModelMap.get(serializedModel.relatedDateModelId) as DateModel;

      (model as TimeModel).relatedDateModel = restored as DateModel;
    }
    if (serializedModel?.relatedTimeModelId !== undefined) {
      (model as DateModel).relatedTimeModel = indexToModelMap.get(serializedModel.relatedTimeModelId) as TimeModel;
    }
    if ((model as any).children) {
      (model as any).children?.forEach(child =>
        this.restoreComponentModelReference(child, indexToModelMap)
      );
    }

    return model;
  }

  public static deserializeConceptModel(
    serializedConceptModel: ConceptModel,
    componentModel: ComponentModel
  ): ConceptModel {
    const conceptModel = new ConceptModel();
    conceptModel.noticeSubTypeCode = serializedConceptModel.noticeSubTypeCode;

    const indexToModelMap = new Map<number, BaseModel>();
    this.fillIndexToModelMap(componentModel, indexToModelMap);
    conceptModel.root = this.restoreConceptNode(
      serializedConceptModel.root,
      null,
      indexToModelMap
    );

    return conceptModel;
  }

  private static restoreConceptNode(
    serializedNode: ConceptNode,
    parent: ConceptNode,
    indexToModelMap: Map<number, BaseModel>
  ): ConceptNode {
    const sourceModel = indexToModelMap.get(serializedNode.sourceModelIndex);

    const node = new ConceptNode(
      serializedNode.businessTerm,
      null,
      serializedNode.metadata,
      sourceModel
    );
    node.value = serializedNode.value;
    node.nativeValue = serializedNode.nativeValue;
    node.nodeIndex = serializedNode.nodeIndex;
    node.qualifier = serializedNode.qualifier;
    node.parent = parent;

    if (serializedNode.children) {
      node.children = serializedNode.children.map(child =>
        this.restoreConceptNode(child, node, indexToModelMap)
      );
    }

    return node;
  }

  private static instantiateModel(serializedModel: SerializedModel): BaseModel {
    const models = [
      FieldModel,
      SectionModel,
      GroupModel,
      CodelistModel,
      AmountModel,
      DateModel,
      TimeModel,
      MetadataOffertypeCodelistModel,
      IdModel,
      IdRefModel,
      MeasureModel,
      IndicatorModel,
      RepeatableFieldModel,
      RepeatableGroupModel,
      MultilingualModel,
      RepeatableSectionModel,
      Bt300BrDeKmuModel,
    ];
    const targetType = models.find(modelType => modelType.name === serializedModel.type);
    if (!targetType) {
      throw `Cant find target type: ${serializedModel.type}`;
    }
    const type = new targetType(null);
    if (!type) {
      throw `Cant find target: ${serializedModel.type}`;
    }
    return type;
  }
}
